// Pure helpers for the Sessions roster page. No React, no side effects.
//
// A "session block" is a doctor's working session (whole AM/PM/EVE block at
// one clinic) served by /api/admin/session-blocks. The roster renders blocks
// (Template mode) or expanded per-date occurrences (Week mode); these helpers
// own label suggestion, cadence badges, chip colors, and the v1 interval
// template generator (alternating firstVisit/followUp micro-slots).

import { hashHue, normalizeHex } from "../slots-heatmap/heatmap-utils";

// Label suggestion by start time: before noon → AM, before 17:30 → PM, else EVE.
// (The 13:00 AM/PM data split lives server-side; the editor suggestion uses
// noon so a 12:30 start reads as PM to a human.)
export function suggestLabelFromTimes(startTime) {
  const match = /^(\d{1,2}):(\d{2})$/.exec(String(startTime || "").trim());
  if (!match) return "AM";
  const minutes = Number(match[1]) * 60 + Number(match[2]);
  if (minutes < 12 * 60) return "AM";
  if (minutes < 17 * 60 + 30) return "PM";
  return "EVE";
}

const ORDINALS = { 1: "1st", 2: "2nd", 3: "3rd", 4: "4th", "-1": "last" };

// Short cadence badge for a block chip: "" for plain weekly, "every 2 wks" for
// week intervals, "1st+4th" / "last" for monthly nth-weekday rotations,
// "day 1+15" for monthly day-of-month.
export function cadenceBadge(recurrence) {
  if (!recurrence || typeof recurrence !== "object") return "";
  const frequency = String(recurrence.frequency || "weekly").toLowerCase();
  if (frequency === "monthly") {
    const weeks = Array.isArray(recurrence.monthWeeks) ? recurrence.monthWeeks : [];
    if (weeks.length > 0) {
      const sorted = [...weeks].sort((a, b) => (a === -1 ? 1 : b === -1 ? -1 : a - b));
      return sorted.map((w) => ORDINALS[w] || String(w)).join("+");
    }
    const days = Array.isArray(recurrence.monthDays) ? recurrence.monthDays : [];
    if (days.length > 0) {
      return `day ${days.map((d) => (d === -1 ? "last" : d)).join("+")}`;
    }
    return "monthly";
  }
  const interval = Number(recurrence.repeatIntervalWeeks) || 1;
  return interval > 1 ? `every ${interval} wks` : "";
}

// ISO weekdays (1=Mon..7=Sun) a block's template covers.
export function blockWeekdays(block) {
  const weekdays = block?.recurrence?.weekdays;
  if (!Array.isArray(weekdays)) return [];
  return weekdays.filter((d) => Number.isInteger(d) && d >= 1 && d <= 7);
}

// Chip color: the block's pinned colorHex wins, otherwise the deterministic
// hue of the "other" entity (location in doctor rows, doctor in location
// rows) — same convention as the calendar session rail.
export function chipTone(entityId, colorHex) {
  const hex = normalizeHex(colorHex);
  if (hex && hex !== "#000000") {
    return { solid: hex, soft: `color-mix(in oklab, ${hex} 16%, transparent)` };
  }
  const hue = hashHue(entityId);
  return {
    solid: `oklch(0.62 0.16 ${hue})`,
    soft: `oklch(0.62 0.16 ${hue} / 0.16)`,
  };
}

// Visual state of a week-mode occurrence chip.
//   normal | cancelled | moved-out (suppressed source) | moved-in (target) | relocated
export function occurrenceState(occ) {
  const action = occ?.override?.action || "";
  if (!action) return "normal";
  if (action === "cancel") return "cancelled";
  if (action === "move") return occ.suppressed ? "moved-out" : "moved-in";
  if (action === "relocate") return "relocated";
  return "normal";
}

const SLOT_MINUTES = 15;

function parseHHMM(value) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(value || "").trim());
  return m ? Number(m[1]) * 60 + Number(m[2]) : NaN;
}

function spanMinutes(startTime, endTime) {
  const start = parseHHMM(startTime);
  const end = parseHHMM(endTime);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return 0;
  return end - start;
}

// "HH:mm" of a template interval's start: block startTime + offsetMin.
export function offsetClock(startTime, offsetMin) {
  const base = parseHHMM(startTime);
  if (!Number.isFinite(base)) return "";
  const total = base + (Number(offsetMin) || 0);
  const h = Math.floor(total / 60) % 24;
  const m = total % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

// The click-cycle for a pattern tick: First visit → Follow-up → Open → …
// Returns the interval with its subType/visitType updated and every other
// key (identity key, metadata like _moreTests/colorHex) preserved.
export function cycleIntervalSubType(interval) {
  const current = String(interval?.subType || "");
  const next = current === "firstVisit" ? "followUp" : current === "followUp" ? "" : "firstVisit";
  const metadata = { ...(interval?.metadata || {}) };
  if (next === "firstVisit") metadata.visitType = "new";
  else if (next === "followUp") metadata.visitType = "followUp";
  else delete metadata.visitType;
  const out = { ...interval, metadata };
  if (next) out.subType = next;
  else delete out.subType;
  if (Object.keys(metadata).length === 0) delete out.metadata;
  return out;
}

// How many 15-minute micro-slots fit in the block span.
export function slotCountForSpan(startTime, endTime) {
  return Math.floor(spanMinutes(startTime, endTime) / SLOT_MINUTES);
}

// Interval layout used by the v1 editor: `total` 15-minute pieces where
// `firstVisitCount` of them are firstVisit, spread as evenly as possible
// between the followUps (the migrated hand-tuned patterns survive untouched —
// this generator only runs for new blocks or when the steppers are used).
export function buildIntervals(startTime, endTime, firstVisitCount, followUpCount) {
  const total = slotCountForSpan(startTime, endTime);
  const fv = Math.max(0, Math.min(total, Number(firstVisitCount) || 0));
  const fu = Math.max(0, Math.min(total - fv, Number(followUpCount) || 0));
  const intervals = [];
  // Even spread: walk an accumulator so e.g. 4 FV in 10 slots lands on
  // positions 1,3,6,8 rather than the first four.
  let credit = 0;
  let fvLeft = fv;
  let fuLeft = fu;
  for (let i = 0; i < total; i += 1) {
    const remaining = total - i;
    credit += fvLeft / remaining;
    let subType;
    if (fvLeft > 0 && (credit >= 1 || fvLeft >= remaining)) {
      subType = "firstVisit";
      fvLeft -= 1;
      credit -= 1;
    } else if (fuLeft > 0) {
      subType = "followUp";
      fuLeft -= 1;
    }
    const interval = {
      offsetMin: i * SLOT_MINUTES,
      durationMin: SLOT_MINUTES,
    };
    if (subType) {
      interval.subType = subType;
      interval.metadata = { visitType: subType === "firstVisit" ? "new" : "followUp" };
    }
    intervals.push(interval);
  }
  return intervals;
}

// Default template for a new block: all 15-minute slots, half firstVisit.
export function buildDefaultIntervals(startTime, endTime) {
  const total = slotCountForSpan(startTime, endTime);
  const fv = Math.floor(total / 2);
  return buildIntervals(startTime, endTime, fv, total - fv);
}

// Count the template's subType mix: {total, firstVisit, followUp, other, durations}.
export function intervalCounts(intervals) {
  const counts = { total: 0, firstVisit: 0, followUp: 0, other: 0, durations: new Set() };
  for (const interval of Array.isArray(intervals) ? intervals : []) {
    counts.total += 1;
    counts.durations.add(Number(interval?.durationMin) || 0);
    const subType = String(interval?.subType || "");
    if (subType === "firstVisit") counts.firstVisit += 1;
    else if (subType === "followUp") counts.followUp += 1;
    else counts.other += 1;
  }
  return counts;
}

// "10 × 15 min · 5 new / 5 follow-up" (durations collapse when uniform).
export function intervalsSummary(intervals) {
  const counts = intervalCounts(intervals);
  if (counts.total === 0) return "No bookable slots";
  const durations = [...counts.durations].filter(Boolean).sort((a, b) => a - b);
  const durationText =
    durations.length === 1 ? `${durations[0]} min` : `${durations.join("/")} min`;
  const mix = [];
  if (counts.firstVisit > 0) mix.push(`${counts.firstVisit} new`);
  if (counts.followUp > 0) mix.push(`${counts.followUp} follow-up`);
  if (counts.other > 0) mix.push(`${counts.other} open`);
  return `${counts.total} × ${durationText}${mix.length ? ` · ${mix.join(" / ")}` : ""}`;
}
